import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import Chiclet from 'components/Chiclet';
import { relatedSearchesVariantSelector } from 'viewModel/selectors/testTarget/relatedSearchesVariantSelector';
import { relatedSearchTermsSelector } from 'selectors/page/relatedSearch/relatedSearchTermsSelector';
import { MAX_RELATED_SEARCH_TERMS, isRelatedSearchesEligible } from 'components/RelatedSearches/constants';
import localeUtils from 'utils/LanguageLocale';
import Debounce from 'utils/Debounce';
import Location from 'utils/Location';
import { space } from 'style/config';
import relatedSearchesEN from 'components/RelatedSearches/locales/relatedSearches_en_US';
import relatedSearchesFR from 'components/RelatedSearches/locales/relatedSearches_fr_CA';

const SEARCH_PATH = '/search';
const DEBOUNCE_MS = 300; // AC-023
const getText = localeUtils.getLocalisedDataFromResource(relatedSearchesEN, relatedSearchesFR);

/**
 * Related Searches section (SBR-3530 shell + SBR-3531 chiclet). Self-contained: resolves its own
 * variant + terms via selectors, applies the SAME eligibility predicate the grid reserves on (so a
 * hidden state never leaves an empty reserved cell), caps to 8 (BR-003), and renders the chiclet rail
 * directly. Returns null in Control / pending / ineligible states (AC-028 render-nothing).
 * SBR-3529 (recap card) / SBR-3532 (chunklet) will branch on `variant` to render their item component.
 */
function RelatedSearches({ productRowsCount }) {
    const variant = useSelector(relatedSearchesVariantSelector);
    const terms = useSelector(relatedSearchTermsSelector, shallowEqual);
    const [tappedIndex, setTappedIndex] = useState(null);

    // Debounced tap-to-search (AC-023): preventDoubleClick fires once, then suppresses repeats within
    // the window. Navigates the same way as the search bar (SiteSearch) / category chiclets.
    const handleTermClick = useMemo(
        () =>
            Debounce.preventDoubleClick(term => {
                // SBR-3535 ANALYTICS HOOK: fire related_search_click here (out of scope this bundle).
                Location.navigateTo(null, `${SEARCH_PATH}?keyword=${encodeURIComponent(term)}`);
            }, DEBOUNCE_MS),
        []
    );

    // SAME predicate as the grid reservation gate: Control / pending / < 3 terms / < 2 rows => render nothing.
    if (!isRelatedSearchesEligible(variant, terms, productRowsCount)) {
        return null;
    }

    const cappedTerms = terms.slice(0, MAX_RELATED_SEARCH_TERMS);
    const title = getText('relatedSearchesTitle');

    return (
        <Box>
            <Text
                is='h2'
                fontSize={['base', 'md']}
                fontWeight='bold'
                lineHeight='tight'
                marginBottom={2}
                children={title}
            />
            <ul
                aria-label={title}
                css={styles.scrollRow}
            >
                {cappedTerms.map((term, i) => (
                    <li key={term}>
                        <Chiclet
                            is='button'
                            type='button'
                            variant='shadow'
                            isActive={tappedIndex === i}
                            aria-pressed={tappedIndex === i}
                            aria-label={getText('searchForTerm', [term])}
                            onClick={() => {
                                setTappedIndex(i);
                                handleTermClick(term);
                            }}
                            children={term}
                        />
                    </li>
                ))}
            </ul>
        </Box>
    );
}

// Horizontal scroll-row recipe (matches SearchChiclets / CategoryChiclets). Co-located per UFE convention.
const styles = {
    scrollRow: {
        margin: `-${space[2]}px -${space.container}px`,
        padding: `${space[2]}px ${space.container}px`,
        fontSize: 0,
        whiteSpace: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        li: {
            display: 'inline-block',
            '+ li': {
                marginLeft: space[2]
            }
        }
    }
};

RelatedSearches.propTypes = {
    productRowsCount: PropTypes.number
};

export default wrapFunctionalComponent(RelatedSearches, 'RelatedSearches');
